import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  FileText,
  Download,
  RefreshCw,
  Clock,
  Settings,
  Archive,
  TrendingUp,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { fetchHealth, fetchAgents } from '../lib/api';

interface DeckTemplate {
  id: string;
  name: string;
  description: string;
  audience: string;
  sections: string[];
  confidentialityLevel: string;
  personaRequired: string;
  estimatedTime: string;
  pageCount: number;
}

interface GeneratedDeck {
  id: string;
  title: string;
  deckType: string;
  personaLevel: string;
  generatedAt: string;
  downloadCount: number;
  status: 'generating' | 'ready' | 'expired';
  confidentialityLevel: string;
}

export default function BriefingDeckGenerator() {
  const [selectedTemplate, setSelectedTemplate] = useState('investor');
  const [personaLevel, setPersonaLevel] = useState('G3X');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDecks, setGeneratedDecks] = useState<GeneratedDeck[]>([]);
  const [templates, setTemplates] = useState<Record<string, DeckTemplate>>({});
  const [metrics, setMetrics] = useState({
    totalDecks: 0,
    lastGenerated: null as string | null,
    mostPopular: 'investor',
    averageGenerationTime: '2.3s'
  });
  const [platformData, setPlatformData] = useState({
    moduleCount: 147,
    agentCount: 35,
    uptime: '99.97%',
    securityStatus: 'Active',
    agentNames: [] as string[],
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load real API data with simulated fallback
  useEffect(() => {
    initializeTemplates();
    loadGeneratedDecks();
    loadMetrics();
    loadFromAPI();
    pollRef.current = setInterval(loadFromAPI, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadFromAPI = async () => {
    const [healthData, agentsData] = await Promise.all([
      fetchHealth(),
      fetchAgents(),
    ]);

    if (healthData || agentsData) {
      setPlatformData(prev => {
        const updated = { ...prev };
        if (healthData) {
          if (healthData.uptime) updated.uptime = typeof healthData.uptime === 'number' ? `${healthData.uptime.toFixed(2)}%` : String(healthData.uptime);
          if (healthData.securityStatus) updated.securityStatus = healthData.securityStatus.status || healthData.securityStatus || 'Active';
        }
        if (agentsData) {
          const agents = Array.isArray(agentsData) ? agentsData : agentsData.agents || [];
          updated.agentNames = agents.slice(0, 10).map((a: any) => a.name || a.id);
          const coreAgents = agents.filter((a: any) => a.type === 'core' || a.role === 'governance');
          if (agents.length > 0) updated.moduleCount = agents.length;
          if (coreAgents.length > 0) updated.agentCount = coreAgents.length;
        }
        return updated;
      });
    }
  };

  const initializeTemplates = () => {
    setTemplates({
      investor: {
        id: 'investor',
        name: 'Investor Deck',
        description: 'VC-ready presentation with financial projections, market analysis, and investment opportunity',
        audience: 'Venture capitalists, angel investors, institutional investors',
        sections: ['Executive Summary', 'Market Opportunity', 'Financial Projections', 'Investment Terms', 'Team & Advisors', 'Call to Action'],
        confidentialityLevel: 'CONFIDENTIAL',
        personaRequired: 'G3X+',
        estimatedTime: '3-5 minutes',
        pageCount: 12
      },
      enterprise: {
        id: 'enterprise',
        name: 'Enterprise Sales Deck',
        description: 'B2B focused presentation highlighting enterprise capabilities and ROI',
        audience: 'Enterprise customers, procurement teams, technical decision makers',
        sections: ['Platform Overview', 'Security & Compliance', 'ROI Analysis', 'Implementation Roadmap', 'Customer Success', 'Next Steps'],
        confidentialityLevel: 'INTERNAL',
        personaRequired: 'G3X+',
        estimatedTime: '2-4 minutes',
        pageCount: 10
      },
      partner: {
        id: 'partner',
        name: 'Partnership Deck',
        description: 'Strategic partnership opportunities and integration possibilities',
        audience: 'Technology partners, channel partners, strategic alliances',
        sections: ['Partnership Overview', 'Integration Opportunities', 'Go-to-Market Strategy', 'Revenue Sharing', 'Technical Requirements', 'Partnership Terms'],
        confidentialityLevel: 'INTERNAL',
        personaRequired: 'G3X+',
        estimatedTime: '2-3 minutes',
        pageCount: 8
      },
      government: {
        id: 'government',
        name: 'Government & Defense Deck',
        description: 'Security-focused presentation for government and defense organizations',
        audience: 'Government agencies, defense contractors, public sector',
        sections: ['Security Framework', 'Compliance Certifications', 'Deployment Architecture', 'Risk Management', 'Support Structure', 'Procurement Process'],
        confidentialityLevel: 'CONFIDENTIAL',
        personaRequired: 'G2X+',
        estimatedTime: '4-6 minutes',
        pageCount: 14
      },
      analyst: {
        id: 'analyst',
        name: 'Analyst Briefing',
        description: 'Technical deep-dive for industry analysts and research firms',
        audience: 'Industry analysts, research firms, technology reporters',
        sections: ['Technical Architecture', 'Competitive Analysis', 'Market Position', 'Innovation Pipeline', 'Industry Impact', 'Research Collaboration'],
        confidentialityLevel: 'INTERNAL',
        personaRequired: 'G3X+',
        estimatedTime: '3-4 minutes',
        pageCount: 11
      }
    });
  };

  const loadGeneratedDecks = () => {
    setGeneratedDecks([
      {
        id: 'deck_1',
        title: 'Agentomy: Enterprise AI Governance Platform',
        deckType: 'investor',
        personaLevel: 'G3X',
        generatedAt: '2026-05-06T09:30:00Z',
        downloadCount: 3,
        status: 'ready',
        confidentialityLevel: 'CONFIDENTIAL'
      },
      {
        id: 'deck_2',
        title: 'Transform Your Enterprise with Agentomy\'s AI Agent Platform',
        deckType: 'enterprise',
        personaLevel: 'G3X',
        generatedAt: '2026-05-06T08:15:00Z',
        downloadCount: 1,
        status: 'ready',
        confidentialityLevel: 'INTERNAL'
      }
    ]);
  };

  const loadMetrics = () => {
    setMetrics({
      totalDecks: 8,
      lastGenerated: '2026-05-06T09:30:00Z',
      mostPopular: 'investor',
      averageGenerationTime: '2.3s'
    });
  };

  const handleGenerateDeck = async () => {
    const template = templates[selectedTemplate];
    if (!template) {
      console.error(`Deck generation failed: unknown template "${selectedTemplate}"`);
      return;
    }

    setIsGenerating(true);

    try {
      // Mock generation process
      await new Promise(resolve => setTimeout(resolve, 2500));

      const newDeck: GeneratedDeck = {
        id: `deck_${Date.now()}`,
        title: `${template.name} -- ${platformData.moduleCount} modules, ${platformData.agentCount} agents, ${platformData.uptime} uptime`,
        deckType: selectedTemplate,
        personaLevel,
        generatedAt: new Date().toISOString(),
        downloadCount: 0,
        status: 'ready',
        confidentialityLevel: template.confidentialityLevel
      };
      
      setGeneratedDecks([newDeck, ...generatedDecks]);
      
      // Trigger download
      handleDownloadDeck(newDeck.id);
      
    } catch (error) {
      console.error('Deck generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadDeck = async (deckId: string) => {
    // Mock download functionality
    const deck = generatedDecks.find(d => d.id === deckId);
    if (deck) {
      // Update download count
      setGeneratedDecks(prev => 
        prev.map(d => 
          d.id === deckId 
            ? { ...d, downloadCount: d.downloadCount + 1 }
            : d
        )
      );
      
      // In real implementation, this would trigger PDF download
      console.log(`Downloading deck: ${deck.title}`);
    }
  };

  const handleRegenerateDeck = async (deckId: string) => {
    const deck = generatedDecks.find(d => d.id === deckId);
    if (deck) {
      setGeneratedDecks(prev => 
        prev.map(d => 
          d.id === deckId 
            ? { ...d, status: 'generating' as const }
            : d
        )
      );

      // Mock regeneration
      setTimeout(() => {
        setGeneratedDecks(prev => 
          prev.map(d => 
            d.id === deckId 
              ? { ...d, status: 'ready' as const, generatedAt: new Date().toISOString() }
              : d
          )
        );
      }, 2000);
    }
  };

  const getPersonaColor = (persona: string) => {
    switch (persona) {
      case 'G1X': return 'text-red-400 bg-red-900 border-red-600';
      case 'G2X': return 'text-orange-400 bg-orange-900 border-orange-600';
      case 'G3X': return 'text-cyan-400 bg-cyan-900 border-cyan-600';
      case 'G4X': return 'text-green-400 bg-green-900 border-green-600';
      default: return 'text-[#A8A29E] bg-gray-900 border-[#4A443D]';
    }
  };

  const getConfidentialityColor = (level: string) => {
    return level === 'CONFIDENTIAL' 
      ? 'text-red-400 bg-red-900 border-red-600'
      : 'text-yellow-400 bg-yellow-900 border-yellow-600';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready': return <CheckCircle className="w-4 h-4 text-ag-active" />;
      case 'generating': return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'expired': return <XCircle className="w-4 h-4 text-ag-critical" />;
      default: return <Clock className="w-4 h-4 text-[#A8A29E]" />;
    }
  };

  const canGenerateTemplate = (template: DeckTemplate) => {
    if (template.personaRequired === 'G2X+') {
      return ['G1X', 'G2X'].includes(personaLevel);
    }
    if (template.personaRequired === 'G3X+') {
      return ['G1X', 'G2X', 'G3X'].includes(personaLevel);
    }
    return true;
  };

  const currentTemplate = templates[selectedTemplate] ?? templates['investor'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
            📊 Briefing Deck Generator
          </h2>
          <p className="text-[#A8A29E]">Generate executive-grade presentations with live data -- {platformData.moduleCount} modules | {platformData.agentCount} agents | {platformData.uptime} uptime | Security {platformData.securityStatus}</p>
        </div>
        <div className="flex items-center space-x-3">
          <Badge variant="outline" className="text-cyan-400 border-cyan-400">
            Auto-Updated Every 24h
          </Badge>
          <Badge variant="outline" className={getPersonaColor(personaLevel)}>
            {personaLevel === 'G1X' ? 'Analyst' : personaLevel === 'G2X' ? 'Builder' : personaLevel === 'G3X' ? 'Operator' : personaLevel === 'G4X' ? 'Strategist' : personaLevel} Access
          </Badge>
        </div>
      </div>

      {/* Generation Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Total Decks</p>
                <p className="text-2xl font-bold text-cyan-400">{metrics.totalDecks}</p>
              </div>
              <FileText className="w-8 h-8 text-cyan-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Most Popular</p>
                <p className="text-2xl font-bold text-green-400 capitalize">{metrics.mostPopular}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Avg Generation</p>
                <p className="text-2xl font-bold text-purple-400">{metrics.averageGenerationTime}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-400 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#A8A29E]">Available Templates</p>
                <p className="text-2xl font-bold text-yellow-400">{Object.keys(templates).length}</p>
              </div>
              <Settings className="w-8 h-8 text-yellow-400 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deck Generation Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Template Selection */}
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">Select Deck Template</h3>
          
          {/* Persona Level Selector */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-[#A8A29E]">Persona Level</label>
            <div className="flex space-x-2">
              {[
                { id: 'G1X', label: 'Analyst' },
                { id: 'G2X', label: 'Builder' },
                { id: 'G3X', label: 'Operator' },
                { id: 'G4X', label: 'Strategist' }
              ].map(({ id: level, label }) => (
                <button
                  key={level}
                  onClick={() => setPersonaLevel(level)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    personaLevel === level
                      ? getPersonaColor(level)
                      : 'text-[#A8A29E] bg-[#292524] border border-[#4A443D] hover:bg-[#3A352F]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Template Cards */}
          <div className="space-y-4">
            {Object.values(templates).map(template => {
              const canGenerate = canGenerateTemplate(template);
              
              return (
                <Card 
                  key={template.id}
                  className={`cursor-pointer transition-all ${
                    selectedTemplate === template.id
                      ? 'bg-cyan-900/20 border-cyan-500'
                      : 'bg-gray-900 border-[#3A352F] hover:bg-[#292524]'
                  } ${!canGenerate ? 'opacity-50' : ''}`}
                  onClick={() => canGenerate && setSelectedTemplate(template.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-semibold text-white">{template.name}</h4>
                      <div className="flex space-x-2">
                        <Badge className={getConfidentialityColor(template.confidentialityLevel)}>
                          {template.confidentialityLevel}
                        </Badge>
                        <Badge variant="outline" className="text-[#A8A29E] border-[#4A443D]">
                          {template.pageCount} pages
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-[#A8A29E] text-sm mb-3">{template.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-[#A8A29E]">Audience:</span>
                        <div className="text-white">{template.audience}</div>
                      </div>
                      <div>
                        <span className="text-[#A8A29E]">Est. Time:</span>
                        <div className="text-white">{template.estimatedTime}</div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <span className="text-[#A8A29E] text-xs">Sections:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {template.sections.slice(0, 3).map((section, index) => (
                          <span key={index} className="text-xs bg-[#292524] text-[#A8A29E] px-2 py-1 rounded">
                            {section}
                          </span>
                        ))}
                        {template.sections.length > 3 && (
                          <span className="text-xs text-[#A8A29E]">+{template.sections.length - 3} more</span>
                        )}
                      </div>
                    </div>

                    {!canGenerate && (
                      <div className="mt-3 text-xs text-ag-critical">
                        Requires {template.personaRequired} persona level or higher
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerateDeck}
            disabled={isGenerating || !currentTemplate || !canGenerateTemplate(currentTemplate)}
            className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
            size="lg"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generating Deck...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Generate Executive Deck
              </>
            )}
          </Button>
        </div>

        {/* Recent Decks */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Recent Decks</h3>
            <Button variant="outline" size="sm" className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]">
              <Archive className="w-4 h-4 mr-2" />
              View All
            </Button>
          </div>

          <div className="space-y-4">
            {generatedDecks.map(deck => (
              <Card key={deck.id} className="bg-gray-900 border-[#3A352F]">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {getStatusIcon(deck.status)}
                        <h4 className="text-white font-medium text-sm">{deck.title}</h4>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-[#A8A29E]">Type:</span>
                          <span className="text-white ml-1 capitalize">{deck.deckType}</span>
                        </div>
                        <div>
                          <span className="text-[#A8A29E]">Generated:</span>
                          <span className="text-white ml-1">
                            {new Date(deck.generatedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-[#A8A29E]">Downloads:</span>
                          <span className="text-white ml-1">{deck.downloadCount}</span>
                        </div>
                        <div>
                          <span className="text-[#A8A29E]">Persona:</span>
                          <Badge className={`ml-1 ${getPersonaColor(deck.personaLevel)} text-xs`}>
                            {deck.personaLevel === 'G1X' ? 'Analyst' : deck.personaLevel === 'G2X' ? 'Builder' : deck.personaLevel === 'G3X' ? 'Operator' : deck.personaLevel === 'G4X' ? 'Strategist' : deck.personaLevel}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDeck(deck.id)}
                        disabled={deck.status !== 'ready'}
                        className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateDeck(deck.id)}
                        disabled={deck.status === 'generating'}
                        className="border-[#4A443D] text-[#A8A29E] hover:bg-[#3A352F]"
                      >
                        <RefreshCw className={`w-4 h-4 ${deck.status === 'generating' ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <Badge className={getConfidentialityColor(deck.confidentialityLevel)}>
                      {deck.confidentialityLevel}
                    </Badge>
                    
                    <div className="text-xs text-[#A8A29E]">
                      {deck.status === 'ready' && 'Ready for download'}
                      {deck.status === 'generating' && 'Generating with live data...'}
                      {deck.status === 'expired' && 'Regeneration required'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {generatedDecks.length === 0 && (
            <Card className="bg-gray-900 border-[#3A352F]">
              <CardContent className="p-8 text-center">
                <FileText className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <h4 className="text-white font-medium mb-2">No decks generated yet</h4>
                <p className="text-[#A8A29E] text-sm">
                  Select a template and generate your first executive briefing deck
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}